import type { WishlistRepository } from '../../domain/repositories/WishlistRepository';
import type { WishlistItem } from '../../domain/entities/WishlistItem';
import type {
  WishlistListResponseDTO,
  WishlistItemResponseDTO,
  ToggleWishlistResponseDTO,
  CheckWishlistResponseDTO,
  ShareWishlistResponseDTO,
  WishlistStatsResponseDTO,
  ClearWishlistResponseDTO,
} from '../../application/dtos/WishlistDTO';
import { httpService } from '@/services/HttpService';

export class HttpWishlistRepository implements WishlistRepository {
  private readonly baseUrl = '/user/wishlist';

  async getWishlist(page = 1, perPage = 15) {
    const response = await httpService.get<WishlistListResponseDTO>(
      `${this.baseUrl}?page=${page}&per_page=${perPage}`
    );
    return {
      items: response.data.map(dto => this.mapToEntity(dto)),
      pagination: {
        total: response.pagination.total,
        perPage: response.pagination.per_page,
        currentPage: response.pagination.current_page,
        lastPage: response.pagination.last_page,
      },
    };
  }

  async addItem(entityType: string, entityId: string): Promise<void> {
    await httpService.post(`${this.baseUrl}/add`, {
      product_entity_type: entityType,
      product_entity_id: entityId,
    });
  }

  async removeItem(entityType: string, entityId: string): Promise<void> {
    await httpService.deleteWithBody(`${this.baseUrl}/remove`, {
      product_entity_type: entityType,
      product_entity_id: entityId,
    });
  }

  async toggleItem(
    entityType: string,
    entityId: string
  ): Promise<{ action: 'added' | 'removed'; inWishlist: boolean }> {
    const response = await httpService.post<ToggleWishlistResponseDTO>(`${this.baseUrl}/toggle`, {
      product_entity_type: entityType,
      product_entity_id: entityId,
    });
    return {
      action: response.data.action,
      inWishlist: response.data.in_wishlist,
    };
  }

  async checkItems(
    items: { entityType: string; entityId: string }[]
  ): Promise<Record<string, boolean>> {
    const response = await httpService.post<CheckWishlistResponseDTO>(`${this.baseUrl}/check`, {
      products: items.map(item => ({
        entity_type: item.entityType,
        entity_id: item.entityId,
      })),
    });
    return response.data.results;
  }

  async clearWishlist(): Promise<{ itemsRemoved: number }> {
    const response = await httpService.delete<ClearWishlistResponseDTO>(`${this.baseUrl}/clear`);
    return {
      itemsRemoved: response.data.items_removed,
    };
  }

  async shareWishlist(): Promise<{ shareUrl: string; expiresAt: string }> {
    const response = await httpService.post<ShareWishlistResponseDTO>(`${this.baseUrl}/share`);
    return {
      shareUrl: response.data.share_url,
      expiresAt: response.data.expires_at,
    };
  }

  async getStats(): Promise<{ itemsCount: number; categoriesCount: number; lastAddedAt: string | null }> {
    const response = await httpService.get<WishlistStatsResponseDTO>(`${this.baseUrl}/stats`);
    return {
      itemsCount: response.data.items_count,
      categoriesCount: response.data.categories_count,
      lastAddedAt: response.data.last_added_at,
    };
  }

  private mapToEntity(dto: WishlistItemResponseDTO): WishlistItem {
    const product = dto.product;
    
    return {
      id: dto.id,
      productEntityType: dto.product_entity_type,
      productEntityId: dto.product_entity_id,
      addedAt: dto.added_at,
      productInfo: product
        ? {
            entityType: product.entity_type,
            entityId: product.entity_id,
            name: product.title ?? 'Unknown Product',
            price: this.toNumber(product.price),
            image: product.thumbnail_url ?? null,
            isActive: product.is_active ?? false,
            slug: product.slug ?? null,
            code: product.code ?? null,
          }
        : null,
    };
  }

  private toNumber(value: number | string | null): number {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return Number.isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  }
}
